import React, { forwardRef, MouseEvent, ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import cn from "classnames";

import './Button.scss';

export interface IButtonProps {
    as?: 'Link' | 'button',
    btnGroupSelected?: boolean,
    children?: ReactNode,
    className?: string;
    disabled?: boolean;
    // Makes the button look disabled, but it still works.
    // This is useful for the SplitButton's toggle button, which is still usable
    // even when the main button is disabled.
    fakeDisabled?: boolean;
    href?: string;
    onClick?: (e?: MouseEvent) => void;
    size?: 'default' | 'small' | 'tiny';
    target?: string,
    theme?: 'red' | 'redOutline' | 'blueOutline'  | 'text' | 'icon';
    type?: 'button' | 'submit';
}

export const Button = forwardRef(({ 
    as,
    btnGroupSelected,
    children,
    className,
    disabled,
    fakeDisabled,
    href,
    onClick,
    size,
    target,
    theme = 'red',
    type = 'button',
}: IButtonProps, ref: React.MutableRefObject<HTMLButtonElement | HTMLAnchorElement>) => {
    const isLink = as === 'Link';

    const classNames = cn(className, {
        cfa_button: true,
        btnGroup_selected: btnGroupSelected,
        red: theme === 'red',
        redOutline: theme === 'redOutline',
        blueOutline: theme === 'blueOutline',
        text: theme === 'text',
        icon: theme === 'icon',
        disabled: disabled || fakeDisabled,
        fakeDisabled: fakeDisabled,
        small: size === 'small',
        tiny: size === 'tiny',
    })

    const onClickInternal = (e: MouseEvent) => {
        if (disabled) {
            // Needed to prevent form submit for forms.
            e.preventDefault();
        } else if (onClick) {
            onClick(e);
        }
    }

    let out: ReactElement;
    if (isLink) {
        out = <Link
            className={classNames}
            onClick={onClickInternal}
            to={href}
            target={target}
            ref={ref as React.MutableRefObject<HTMLAnchorElement>}
        >
            {children}
        </Link>
    } else {
        out = <button
            className={classNames}
            onClick={onClickInternal}
            type={type}
            disabled={disabled}
            ref={ref as React.MutableRefObject<HTMLButtonElement>}
        >
            {children}
        </button>
    }

    // For icon buttons, we need to use flexbox on the button to center the contents,
    // so we need an inline-block wrapper to still act like an inline button.
    if (theme === 'icon') {
        out = (<div className="buttonIconWrap">
            {out}
        </div>)
    }

    return out;
});